import {DataFetcher, useGrpcRequest} from '@aboutyou/data-fetcher';
import {CategoryMetaService_GetPageMetaDataLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/meta/service.grpc.lazy';
import {GetPageMetaDataResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/category_page/v1/meta/service.types';
import {Head} from '@aboutyou/head-elements';
import {
  RouterErrorHandler,
  createRedirectHint,
  useQueryParamsCallback,
  useRecentFullUrl,
} from '@aboutyou/router';
import {TrackContentPageView} from '@aboutyou/tracking';
import React, {FC, memo} from 'react';
import {useAnyFilterFlyoutOpened} from '../../../features/Filters/shared/useAnyFilterFlyoutOpened';
import {getPagination} from '../../../features/ProductStream/helpers';
import {useAppliedPotentialFilters} from '../../../features/ProductStream/utils';
import {
  ensure404Page,
  extractMetaAndLinkTags,
  isSeoData,
  setDataTestIds,
} from '../../../shared-deprecated/metaUtils';
import {useEnsureRedirectData} from '../../../shared-deprecated/useEnsureRedirectData';
import {useUpdateTargetGroup} from '../../../shared-deprecated/useSelectedTargetGroup';
import {useCategoryIdentifier} from '../utils';
import {useLegacyPath} from '../utils/useCategoryIdentifier';

/**
 * Renders all meta information and handles basic tracking events of the Category Page. This component is also responsible for redirects and 404 handling.
 */
export const CategoryHeadSeo: FC<React.PropsWithChildren> = props => {
  return (
    <DataFetcher
      request={useSeoByPage()}
      Error={RouterErrorHandler}
      View={MemoView}
      viewProps={props}
    />
  );
};

export const useSeoByPage = () => {
  const ensureDefaultRedirectData = useEnsureRedirectData();
  const fullUrl = useRecentFullUrl();
  const categoryIdentifier = useCategoryIdentifier();
  const appliedFilters = useAppliedPotentialFilters();
  const isFilterFlyoutOpen = useAnyFilterFlyoutOpened();
  const getQueryData = useQueryParamsCallback();

  const ensureRedirectData: typeof ensureDefaultRedirectData = data => {
    const queryParam = getQueryData({includeUtmParams: true});
    const isBraFinderPage =
      getQueryData().get('open') === 'filterQuestionnaire' &&
      categoryIdentifier.id === 195822;
    const hasNikeBrandFilter = Boolean(
      getQueryData().get('brand')?.includes('nike-272'),
    );

    // The app implemented a bra finder which should be advertised through marketing. The bra finder does not exists on the web so far.
    // Those redirects should usually happen in the backend, as capacities are low we hack it into the frontend for now.
    if (isBraFinderPage && !hasNikeBrandFilter) {
      queryParam.set('brand', 'nike-272');
      throw createRedirectHint(
        `/c/frauen/sport/sportbekleidung/sportwaesche/sport-bhs-195822?${queryParam.toString()}`,
        302,
      );
    }
    return ensureDefaultRedirectData(data);
  };

  return useGrpcRequest(
    CategoryMetaService_GetPageMetaDataLazy,
    {
      category: {
        identifier: categoryIdentifier,
      },
      pagination: getPagination(1),
      appliedFilters: {
        filters: appliedFilters,
        quickFilter: {},
      },
      url: fullUrl,
    },
    {
      keepPreviousData: false,
      disabled: process.browser && isFilterFlyoutOpen,
      validateData: ensureRedirectData,
      validateError: ensure404Page,
    },
  );
};

const View: FC<GetPageMetaDataResponse> = ({meta, recommendedTargetGroup}) => {
  const {isTargetGroupUpdating} = useUpdateTargetGroup(recommendedTargetGroup);
  const legacyCategoryPath = useLegacyPath();

  if (!isSeoData(meta)) {
    return null;
  }

  const seoData = meta.type.seoData;
  const [metaTags, linkTags] = setDataTestIds(
    extractMetaAndLinkTags(seoData.elements),
  );

  return (
    <>
      {!isTargetGroupUpdating && Boolean(legacyCategoryPath) && (
        <TrackContentPageView
          breadcrumbsPath={legacyCategoryPath}
          pageTitle={seoData.title}
        />
      )}
      <Head meta={metaTags} link={linkTags}>
        <title data-testid="MetaTitle">{seoData.title}</title>
      </Head>
    </>
  );
};
const MemoView = memo(View);
